import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { F, Sel } from "@/components/admin/RatesAdmin";
import { fmtNum, CURRENCIES } from "@/lib/constants";

const empty = {
  shipping_line_id: "", shipment_type: "export", port_id: "all", charge_type: "",
  currency: "INR", amount: 0, tax_percent: 18, valid_from: "", valid_until: "",
  container_type: "all", container_size: "all", remarks: "", active: true,
};

const EQUIP = [
  { v: "all", l: "All Equipment" }, { v: "dry", l: "Dry" }, { v: "reefer", l: "Reefer" },
  { v: "ot", l: "Open Top (OT)" }, { v: "fr", l: "Flat Rack (FR)" },
];

export default function ChargesAdmin() {
  const [charges, setCharges] = useState([]);
  const [lines, setLines] = useState([]);
  const [ports, setPorts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filterLine, setFilterLine] = useState("all");
  const [stype, setStype] = useState("export");

  const load = () => {
    const q = new URLSearchParams({ shipment_type: stype });
    if (filterLine !== "all") q.set("shipping_line_id", filterLine);
    api.get(`/local-charges?${q.toString()}`).then(({ data }) => setCharges(data));
  };
  useEffect(() => { api.get("/shipping-lines").then(({ data }) => setLines(data)); api.get("/ports").then(({ data }) => setPorts(data)); }, []);
  useEffect(() => { load(); }, [filterLine, stype]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.shipping_line_id || !form.charge_type) { toast.error("Line and charge type required"); return; }
    const payload = {
      ...form, amount: Number(form.amount), tax_percent: Number(form.tax_percent),
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : new Date().toISOString(),
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : new Date(Date.now() + 30 * 864e5).toISOString(),
    };
    try {
      if (editId) await api.put(`/local-charges/${editId}`, payload);
      else await api.post("/local-charges", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { await api.delete(`/local-charges/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex flex-wrap justify-between gap-3 mb-4">
        <div className="flex gap-2">
          <Select value={stype} onValueChange={setStype}>
            <SelectTrigger className="h-9 w-40 rounded-sm" data-testid="ca-stype"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="export">Export</SelectItem><SelectItem value="import">Import</SelectItem></SelectContent>
          </Select>
          <Select value={filterLine} onValueChange={setFilterLine}>
            <SelectTrigger className="h-9 w-52 rounded-sm" data-testid="ca-line"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Lines</SelectItem>{lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button size="sm" className="bg-signal text-white rounded-sm" onClick={() => { setForm({ ...empty, shipment_type: stype }); setEditId(null); setOpen(true); }} data-testid="add-charge-btn"><Plus className="h-4 w-4 mr-1.5" />Add Charge</Button>
      </div>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-navy text-white text-xs uppercase">
            <th className="text-left px-3 py-2.5">Line</th><th className="text-left px-3 py-2.5">Charge</th>
            <th className="text-left px-3 py-2.5">Port</th>
            <th className="text-center px-3 py-2.5">Equip</th>
            <th className="text-center px-3 py-2.5">Size</th>
            <th className="text-right px-3 py-2.5">Amount</th><th className="text-center px-3 py-2.5">Tax</th>
            <th className="text-left px-3 py-2.5">Valid Until</th><th></th>
          </tr></thead>
          <tbody>
            {charges.map((c) => (
              <tr key={c.id} className="border-t border-border hover:bg-secondary/50" data-testid={`admin-charge-${c.id}`}>
                <td className="px-3 py-2.5 text-navy">{c.shipping_line_name}</td>
                <td className="px-3 py-2.5">{c.charge_type}</td>
                <td className="px-3 py-2.5 text-xs">{c.port_name || "All Ports"}</td>
                <td className="px-3 py-2.5 text-center text-xs capitalize">{c.container_type === "all" || !c.container_type ? "All" : c.container_type}</td>
                <td className="px-3 py-2.5 text-center font-mono-data text-xs">{c.container_size === "all" || !c.container_size ? "All" : `${c.container_size}ft`}</td>
                <td className="px-3 py-2.5 text-right font-mono-data">{c.currency} {fmtNum(c.amount)}</td>
                <td className="px-3 py-2.5 text-center font-mono-data">{c.tax_percent}%</td>
                <td className="px-3 py-2.5 font-mono-data text-xs">{(c.valid_until || "").slice(0, 10)}</td>
                <td className="px-3 py-2.5"><div className="flex gap-1 justify-end">
                  <button onClick={() => { setForm({ ...c, valid_from: (c.valid_from || "").slice(0, 10), valid_until: (c.valid_until || "").slice(0, 10) }); setEditId(c.id); setOpen(true); }} className="p-1.5 hover:bg-secondary rounded-sm" data-testid={`edit-charge-${c.id}`}><Pencil className="h-3.5 w-3.5 text-navy" /></button>
                  <button onClick={() => del(c.id)} className="p-1.5 hover:bg-secondary rounded-sm" data-testid={`del-charge-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "Add"} Local Charge</DialogTitle><DialogDescription className="sr-only">Local charge details form</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Shipping Line"><Sel value={form.shipping_line_id} onChange={(v) => set("shipping_line_id", v)} items={lines.map((l) => ({ v: l.id, l: l.name }))} testid="cf-line" /></F>
            <F label="Shipment Type"><Sel value={form.shipment_type} onChange={(v) => set("shipment_type", v)} items={[{ v: "export", l: "Export" }, { v: "import", l: "Import" }]} testid="cf-stype" /></F>
            <F label="Charge Type"><Input value={form.charge_type} onChange={(e) => set("charge_type", e.target.value)} placeholder="THC, Documentation…" className="rounded-sm" data-testid="cf-type" /></F>
            <F label="Amount"><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="rounded-sm font-mono-data" data-testid="cf-amount" /></F>
            <F label="Port"><Sel value={form.port_id} onChange={(v) => set("port_id", v)} items={[{ v: "all", l: "All Ports" }, ...ports.map((p) => ({ v: p.id, l: `${p.name}, ${p.country}` }))]} testid="cf-port" /></F>
            <F label="Equipment"><Sel value={form.container_type} onChange={(v) => set("container_type", v)} items={EQUIP} testid="cf-equip" /></F>
            <F label="Container Size"><Sel value={form.container_size} onChange={(v) => set("container_size", v)} items={[{ v: "all", l: "All Sizes" }, { v: "20", l: "20 ft" }, { v: "40", l: "40 ft" }]} testid="cf-size" /></F>
            <F label="Currency"><Sel value={form.currency} onChange={(v) => set("currency", v)} items={CURRENCIES.map((c) => ({ v: c, l: c }))} testid="cf-currency" /></F>
            <F label="Tax %"><Input type="number" value={form.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} className="rounded-sm font-mono-data" data-testid="cf-tax" /></F>
            <F label="Valid From"><Input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} className="rounded-sm font-mono-data" data-testid="cf-vfrom" /></F>
            <F label="Valid Until"><Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} className="rounded-sm font-mono-data" data-testid="cf-vuntil" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-signal text-white rounded-sm" onClick={save} data-testid="cf-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
